import { ArrowLeft } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";

export interface BackButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Testo accanto alla freccia. Senza, resta la sola freccia e `aria-label` è obbligatorio. */
  label?: React.ReactNode;
}

/**
 * BackButton — l'unico modo di tornare indietro in Ando.
 *
 * Nasce da cinque implementazioni a mano che dicevano la stessa cosa in cinque
 * modi: quadratini da 24, 28 e 32px, tre veli di hover diversi (`white/55`,
 * `orange-soft/40`, `white`) e due anelli di focus (`ando-orange`, `accent`).
 * Una destinazione che l'utente impara una volta deve avere una forma sola.
 *
 * **Nessuna chrome a riposo**: niente bordo, niente riempimento, niente
 * pillola. È il principio tipografico di `DESIGN.md` §3 — nella chrome di
 * navigazione lo stato è neutro e la gerarchia la fa il testo, non un box.
 *
 * All'hover succedono due cose e solo due: il testo sale di un gradino
 * (`ink-2` → `ink-1`) e la freccia scivola di 2px **nella sua direzione**.
 * Il movimento dice "di là", non è decorazione — è lo stesso patto di
 * `OpensInPanel`, che scivola in avanti. Sotto `motion-safe:`, perché
 * `prefers-reduced-motion` va rispettato anche quando il gesto è minimo.
 *
 * L'anello di focus resta arancione: lì il colore è l'unico segnale
 * disponibile, e il margine negativo gli dà respiro senza spostare il testo
 * dal suo allineamento ottico con il contenuto sotto.
 *
 * Solo `<button>`: le destinazioni che oggi usano un `<Link>` (dettaglio
 * utente, dettaglio organizzazione) si convergeranno qui quando le tocchiamo,
 * ed è allora che si aggiunge il ramo `asChild` — con un consumatore vero che
 * lo eserciti, non prima.
 */
export const BackButton = React.forwardRef<HTMLButtonElement, BackButtonProps>(
  ({ label, className, ...rest }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        "group -mx-1 inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-1 text-ink-2 transition-colors hover:text-ink-1",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1",
        label && "font-medium text-[13px]",
        className,
      )}
      {...rest}
    >
      <ArrowLeft
        size={label ? 15 : 16}
        strokeWidth={1.75}
        aria-hidden
        className="motion-safe:-translate-x-0 motion-safe:transition-transform motion-safe:duration-150 motion-safe:ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:motion-safe:-translate-x-0.5"
      />
      {label ? <span>{label}</span> : null}
    </button>
  ),
);
BackButton.displayName = "BackButton";
